import ConfirmationModal, { DeletionTarget, Disclaimer } from '@components/ConfirmationModal'
import { useCreateIdea, useDeleteIdea, useUpdateIdea } from '@hooks'
import { getErrorMessage } from '@lib/utils'
import { EventCategory } from '@orbit/api-types'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import AddIdeaModal from '../AddIdeaModal'
import CategoryColumn from '../CategoryColumn'
import EditIdeaModal from '../EditIdeaModal'
import { CategoryColumn as CategoryColumnType, Idea } from '../types'
import { organizeByCategorycolumns } from '../utils'
import { BoardContainer, ColumnsContainer, ScrollableColumnsWrapper } from './Board.styles'

interface BoardProps {
	tripId: string
	ideas: Idea[]
}

const Board: React.FC<BoardProps> = ({ tripId, ideas }) => {
	const deleteIdeaMutation = useDeleteIdea()
	const createIdeaMutation = useCreateIdea()
	const updateIdeaMutation = useUpdateIdea()

	const [isAddModalOpen, setIsAddModalOpen] = useState(false)
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const [selectedCategory, setSelectedCategory] = useState<EventCategory | undefined>(undefined)
	const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null)

	const columns: CategoryColumnType[] = organizeByCategorycolumns(ideas)

	const handleAddIdea = (category: string) => {
		setSelectedCategory(category as EventCategory)
		setIsAddModalOpen(true)
	}

	const handleCreateIdea = async (ideaData: {
		name: string
		description?: string
		category?: EventCategory
		location?: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		}
		links?: string[]
	}) => {
		try {
			await createIdeaMutation.mutateAsync({
				tripId,
				ideaData: {
					name: ideaData.name,
					description: ideaData.description || null,
					category: ideaData.category || "Other",
					location: ideaData.location || null,
					links: ideaData.links || [],
					tags: [],
					tripId: parseInt(tripId),
					userId: null,
				},
			})
			toast.success('Idea added successfully!')
		} catch (error) {
			console.error('Failed to create idea:', error)
			const errorMessage = getErrorMessage(error, 'Failed to add idea. Please try again.')
			toast.error(errorMessage)
		}
	}

	const handleEditIdea = (ideaId: string) => {
		const idea = ideas.find(b => b.id.toString() === ideaId)
		if (idea) {
			setSelectedIdea(idea)
			setIsEditModalOpen(true)
		}
	}

	const handleUpdateIdea = async (ideaData: {
		name: string
		description?: string
		category?: EventCategory
		location?: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		}
		links?: string[]
	}) => {
		if (!selectedIdea) return

		try {
			await updateIdeaMutation.mutateAsync({
				tripId,
				ideaId: selectedIdea.id.toString(),
				ideaData: {
					name: ideaData.name,
					description: ideaData.description || null,
					category: ideaData.category || null,
					location: ideaData.location || null,
					links: ideaData.links || [],
				},
			})
			toast.success('Idea updated successfully!')
		} catch (error) {
			console.error('Failed to update idea:', error)
			const errorMessage = getErrorMessage(error, 'Failed to update idea. Please try again.')
			toast.error(errorMessage)
		}
	}

	const handleDeleteClick = () => {
		setIsDeleteModalOpen(true)
	}

	const handleDeleteConfirm = async () => {
		if (!selectedIdea) return
		try {
			await deleteIdeaMutation.mutateAsync({
				tripId,
				ideaId: selectedIdea.id.toString(),
			})
			setIsDeleteModalOpen(false)
			setIsEditModalOpen(false)
			toast.success('Idea deleted successfully!')
		} catch (error) {
			console.error('Failed to delete idea:', error)
			const errorMessage = getErrorMessage(error, 'Failed to delete idea. Please try again.')
			toast.error(errorMessage)
		}
	}

	const handleDeleteCancel = () => {
		setIsDeleteModalOpen(false)
	}

	return (
		<>
			<BoardContainer>
				<ScrollableColumnsWrapper>
					<ColumnsContainer>
						{columns.map(column => (
							<CategoryColumn
								key={column.category}
								column={column}
								onAddIdea={handleAddIdea}
								onEditIdea={handleEditIdea}
							/>
						))}
					</ColumnsContainer>
				</ScrollableColumnsWrapper>
			</BoardContainer>

			<AddIdeaModal
				isOpen={isAddModalOpen}
				onClose={() => setIsAddModalOpen(false)}
				onSubmit={handleCreateIdea}
				categoryFilter={selectedCategory}
			/>

			<EditIdeaModal
				isOpen={isEditModalOpen}
				onClose={() => setIsEditModalOpen(false)}
				onSubmit={handleUpdateIdea}
				onDelete={selectedIdea ? handleDeleteClick : undefined}
				idea={selectedIdea}
			/>

			<ConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={handleDeleteCancel}
				onConfirm={handleDeleteConfirm}
				title="Delete Idea"
				message={
					<>
						Are you sure you want to delete '<DeletionTarget>{selectedIdea?.name}</DeletionTarget>'?
						<Disclaimer>This action cannot be undone</Disclaimer>
					</>
				}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>
		</>
	)
}

export default Board
